<template>
  <vx-card class="mt-base">
    <heading size="h2" style-size="h4" class="mb-5">{{ activityTitle }}</heading>
    <p class="mb-5">Remember the Perceived Stress Scale (PSS)? Well, it’s time to put it to use!</p>
    <p class="mb-5">
      When a psychologist does a test or assessment with a client, we say they ‘administer’ the test
      or assessment. Once they’ve administered the test or assessment, they then interpret the
      results.
    </p>
    <p class="mb-5">
      As we are not trained and registered psychologists (yet!), we aren’t qualified to interpret
      the results, so remember that the explanations below are just approximations. If you feel you,
      or someone else who does the test, is suffering from severe stress you should seek help from a
      health professional.
    </p>
    <p class="mb-5">
      Now, it’s time to do the test. You should do the test first with yourself and then, if
      possible, with a friend or family member.
    </p>
    <p class="mb-5">
      To administer the test, you need to answer the 10 questions below. Don’t overthink it – it’s
      just how you feel right now.
    </p>

    <div class="vx-row flex items-center justify-center my-5 scale-header">
      <div class="vx-col xl:w-1/8 lg:w-1/6 w-full mb-2">0 = Never</div>
      <div class="vx-col xl:w-1/8 lg:w-1/6 w-full mb-2">1 = Almost Never</div>
      <div class="vx-col xl:w-1/8 lg:w-1/6 w-full mb-2">2 = Sometimes</div>
      <div class="vx-col xl:w-1/8 lg:w-1/6 w-full mb-2">3 = Fairly Often</div>
      <div class="vx-col xl:w-1/8 lg:w-1/6 w-full mb-2">4 = Very Often</div>
    </div>

    <div v-for="(question, index) in questions" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div
          class="vx-col xl:w-1/8 lg:w-1/6 w-full mb-2 focusable-parent"
          v-for="(answer, index2) in question.answers"
          :key="index2"
          v-if="isEditable"
        >
          <vs-radio
            :vs-name="'answer_' + index"
            v-model="answers['question' + index]"
            class="checkbox_answer multiline focus-visible-style"
            :vs-value="answer"
            >{{ index2 }}
          </vs-radio>
        </div>
        <div class="my-2 mx-5" v-else>
          <vs-radio
            :vs-name="'answer_' + index"
            v-model="answers['question' + index]"
            class="checkbox_answer multiline"
            :vs-value="answer"
            disabled="true"
            >{{ index2 }}
          </vs-radio>
        </div>
      </div>
    </div>

    <div class="overflow-hidden mb-5">
      <div>
        <label>
          Your score is:
          <vs-input v-model="score" class="mr-5" />
        </label>
      </div>

      <button
        class="btn btn-primary btn-size-medium mt-3"
        type="border"
        @click="calculateScore"
        v-if="isEditable"
      >
        Calculate Score
      </button>
    </div>

    <heading size="h2" style-size="h4" class="mb-5">What does my score mean?</heading>
    <p class="mb-5">
      Scores on the PSS range from 0 to 40. The higher the number, the higher the perceived stress
      of the person who took the test.
    </p>
    <p class="mb-5">
      While it’s important to note that there are no hard and fast rules on how to interpret PSS
      scores, scores around 13 are considered about average. With that in mind, here is one
      interpretation of the scores:
    </p>

    <table class="table default-table text-center">
      <thead>
        <tr class="header">
          <th scope="col" width="50%">Score</th>
          <th scope="col">Stress Levels</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>0 - 13</td>
          <td>Low Perceived Stress</td>
        </tr>
        <tr>
          <td>14 - 26</td>
          <td>Moderate Perceived Stress</td>
        </tr>
        <tr>
          <td>27 - 40</td>
          <td>High Perceived Stress</td>
        </tr>
      </tbody>
    </table>
    <p class="mb-5">How did you / your volunteer do?</p>
    <p class="mb-5">
      Regardless of the score, stress management techniques are beneficial for everyone.
    </p>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videoTrigger1: require('@/assets/images/experiences/hairdressing/hair_structure_splash.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/hairdressing/hair_structure.mp4',
      },
      login_type: localStorage.getItem('login_type'),
      file: '',
      questions: {
        1: {
          question:
            'In the last month, how often have you been upset because of something that happened unexpectedly?',
          answers: [0, 1, 2, 3, 4],
        },
        2: {
          question:
            'In the last month, how often have you felt that you were unable to control the important things in your life?',
          answers: [0, 1, 2, 3, 4],
        },
        3: {
          question: 'In the last month, how often have you felt nervous and "stressed"?',
          answers: [0, 1, 2, 3, 4],
        },
        4: {
          question:
            'In the last month, how often have you felt confident about your ability to handle your personal problems?',
          answers: [4, 3, 2, 1, 0],
        },
        5: {
          question: 'In the last month, how often have you felt that things were going your way?',
          answers: [4, 3, 2, 1, 0],
        },
        6: {
          question:
            'In the last month, how often have you found that you could not cope with all the things that you had to do?',
          answers: [0, 1, 2, 3, 4],
        },
        7: {
          question:
            'In the last month, how often have you been able to control irritations in your life?',
          answers: [4, 3, 2, 1, 0],
        },
        8: {
          question: 'In the last month, how often have you felt that you were on top of things?',
          answers: [4, 3, 2, 1, 0],
        },
        9: {
          question:
            'In the last month, how often have you been angered because of things that were outside of your control?',
          answers: [0, 1, 2, 3, 4],
        },
        10: {
          question:
            'In the last month, how often have you felt difficulties were piling up so high that you could not overcome them?',
          answers: [0, 1, 2, 3, 4],
        },
      },
      answers: {
        question1: null,
        question2: null,
        question3: null,
        question4: null,
        question5: null,
        question6: null,
        question7: null,
        question8: null,
        question9: null,
        question10: null,
      },
      score: 0,
    };
  },
  methods: {
    save() {
      this.data.question1 = JSON.stringify(this.answers);
      this.saveActivity(this.$route.params.slug, this.data, this.file, 'doc');
    },
    calculateScore() {
      let score = 0;
      for (let i = 1; i <= 10; i++) {
        score += this.answers['question' + i];
      }
      this.score = score;
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question1) {
      this.answers = JSON.parse(this.data.question1);
      this.calculateScore();
    }
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}

.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}

.scale-header {
  background: #f5f5f5;
  padding-top: 5px;
  font-weight: 600;
}
</style>
